import { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Tooltip,
  Space,
  Switch,
  Row,
  Col,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { BOM } from "../../../../../services/definedMethod";

export default function InterestContent({
  form,
  onNext,
  onPrevious,
  interbankTemplate,
  interestTypeOptions,
}) {
  const [selectedInterestType, setSelectedInterestType] = useState(null);
  const [taxGroup, setTaxGroup] = useState(false);

  // Initialize state from form values (for both create and update scenarios)
  useEffect(() => {
    const formValues = form.getFieldsValue();

    // Initialize interest type
    if (formValues.interestType !== undefined) {
      setSelectedInterestType(formValues.interestType);
    }

    // Initialize tax - check both possible field names
    const isTaxGroupEnabled =
      formValues.allowTax ||
      (formValues.taxGroup !== undefined && formValues.taxGroup !== null);
    setTaxGroup(isTaxGroupEnabled);
  }, [form]);

  // Watch for form field changes (useful for external form updates)
  useEffect(() => {
    const subscription = form.getFieldsValue();

    // Update local state when form values change externally
    if (subscription.interestType !== selectedInterestType) {
      setSelectedInterestType(subscription.interestType);
    }

    const isTaxGroupEnabled =
      subscription.allowTax ||
      (subscription.taxGroup !== undefined && subscription.taxGroup !== null);
    if (isTaxGroupEnabled !== taxGroup) {
      setTaxGroup(isTaxGroupEnabled);
    }
  }, []);

  const handleInputChange = (field, value) => {
    form.setFieldValue(field, value);
  };

  const handleInterestTypeChange = (value) => {
    console.log("Interest type changed:", value);
    setSelectedInterestType(value);
    handleInputChange("interestType", value);

    // Auto-populate interestRateType with the same value
    form.setFieldsValue({
      interestRateType: value,
    });
  };

  const handleTaxGroupChange = (checked) => {
    console.log("Tax changed:", checked);
    setTaxGroup(checked);

    // Set the allowTax field
    handleInputChange("allowTax", checked);

    if (checked) {
      // If enabling tax, preserve existing selection if any
      const currentSelection = form.getFieldValue("taxGroup");
      if (currentSelection === undefined || currentSelection === null) {
        // Only clear if there's no existing value (for create scenario)
        handleInputChange("taxGroup", undefined);
      }
    } else {
      // If disabling tax, clear the selection
      handleInputChange("taxGroup", undefined);
      form.setFieldValue("taxGroup", undefined);
    }
  };

  const handleTaxGroupSelectionChange = (value) => {
    console.log("Tax selection changed:", value);
    handleInputChange("taxGroup", value);
  };

  // Formula display component
  const FormulaDisplay = ({ type }) => {
    if (type === 0) {
      return (
        <div
          style={{
            background: "#F9FAFB",
            padding: "16px",
            borderRadius: "8px",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "16px",
              width: "100%",
              position: "relative",
            }}
          >
            <div>SI = P × R × (T ÷ D)</div>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "4px" }}
            >
              <div>• P = Principal amount</div>
              <div>• R = Annual Interest Rate (%)</div>
              <div>• T = Tenor in Days</div>
              <div>• D = Day Count Basis</div>
            </div>
          </div>
        </div>
      );
    }

    if (type === 1) {
      return (
        <div
          style={{
            background: "#F9FAFB",
            padding: "16px",
            borderRadius: "8px",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "32px",
            }}
          >
            <div style={{ flex: "0 0 auto" }}>
              <div>
                A = P × (1 + R/n×100)<sup>nt</sup>
              </div>
              <div style={{ marginTop: "4px" }}>CI = A - P</div>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                color: "#667085",
              }}
            >
              <div>• A = Final amount</div>
              <div>• P = Principal amount</div>
              <div>• R = Annual Interest Rate (%)</div>
              <div>• n = Number of compounding periods per year</div>
              <div>• T = Time (in years)</div>
            </div>
          </div>
        </div>
      );
    }

    if (type === 2) {
      return (
        <div
          style={{
            background: "#F9FAFB",
            padding: "16px",
            borderRadius: "8px",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            <div
              style={{
                color: "#667085",
                fontSize: "14px",
              }}
            >
              • Used in car loans, personal loans, and consumer durable loans.
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "32px",
              }}
            >
              <div style={{ flex: "0 0 auto" }}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <div>
                    P × R × (1+R)<sup>n</sup>
                  </div>
                  <div>EMI = ───────────────</div>
                  <div>
                    (1+R)<sup>n</sup> -1
                  </div>
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  color: "#667085",
                }}
              >
                <div>• P = Principal amount</div>
                <div>• R = Monthly interest rate (annual rate /12/100)</div>
                <div>• N = Loan tenure in months</div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (type === 3) {
      return (
        <div
          style={{
            background: "#F9FAFB",
            padding: "16px",
            borderRadius: "8px",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              color: "#667085",
            }}
          >
            <div>
              • Interest is calculated on the outstanding Principal, not the
              original loan amount.
            </div>
            <div>• Used in home loans, personal loans, etc</div>
            <div>
              • Interest reduces as you pay off the loan, making total interest
              lower than the EMI method.
            </div>
          </div>
        </div>
      );
    }

    if (type === 4) {
      return (
        <div
          style={{
            background: "#F9FAFB",
            padding: "16px",
            borderRadius: "8px",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "24px",
            }}
          >
            <div>
              <div style={{ marginBottom: "8px" }}>Formula:</div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "16px",
                }}
              >
                <div>Total Interest =</div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <div>P × R × T</div>
                  <div>100</div>
                </div>
              </div>

              <div style={{ marginBottom: "8px" }}>EMI Calculation</div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <div>EMI =</div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <div>P + Total Interest</div>
                  <div style={{ borderTop: "1px solid #667085" }}>N</div>
                </div>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                color: "#667085",
              }}
            >
              <div>
                • Interest is charged on the original principal amount
                throughout the loan tenure
              </div>
              <div>
                • Used in car loans, personal loans, and consumer durable loans.
              </div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const handleNext = async () => {
    try {
      // Validate required fields
      const fieldsToValidate = [
        "interestType",
        "interestRateType",
        "defaultRate",
        "interestCalculationBasis",
        "interestPostingType",
      ];

      // If tax is enabled, also validate the tax selection
      if (taxGroup) {
        fieldsToValidate.push("taxGroup");
      }

      await form.validateFields(fieldsToValidate);
      onNext();
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  return (
    <Card
      style={{
        borderRadius: "12px",
        boxShadow:
          "0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          marginBottom: "32px",
        }}
      >
        <h1
          style={{
            fontWeight: 700,
            fontSize: "24px",
            lineHeight: "32px",
            color: "#101828",
            marginBottom: "0px",
          }}
        >
          Interest
        </h1>
        <p
          style={{
            fontWeight: 400,
            fontSize: "14px",
            lineHeight: "20px",
            color: "#344054",
            marginTop: "0px",
          }}
        >
          Set Interest Parameters
        </p>
      </div>

      <Form layout="vertical" requiredMark={false} form={form}>
        <Row>
          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <Space>
                  Interest Type
                  <Tooltip title="Interest type information">
                    <InfoCircleOutlined style={{ color: "#98A2B3" }} />
                  </Tooltip>
                </Space>
              }
              name="interestType"
              rules={[
                { required: true, message: "Please select interest type" },
              ]}
              validateTrigger={["onChange", "onBlur"]}
            >
              <Select
                placeholder="Select interest type"
                style={{ height: "44px" }}
                onChange={handleInterestTypeChange}
                options={interestTypeOptions}
                value={selectedInterestType}
              />
            </Form.Item>

            {selectedInterestType !== null &&
              selectedInterestType !== undefined && (
                <FormulaDisplay type={selectedInterestType} />
              )}
          </Col>

          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <Space>
                  Interest Method
                  <Tooltip title="Interest rate information">
                    <InfoCircleOutlined style={{ color: "#98A2B3" }} />
                  </Tooltip>
                </Space>
              }
              name="interestRateType"
              style={{ flex: 1 }}
            >
              <Select
                placeholder="Select interest method"
                style={{ width: "100%", height: "44px" }}
                options={interestTypeOptions}
                disabled
                className="of-bold-disable-input read-only-select"
              />
            </Form.Item>
          </Col>

          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label="Default Rate"
              name="defaultRate"
              style={{ flex: 1 }}
              rules={[
                { required: true, message: "Please enter default rate" },
                {
                  pattern: /^\d*\.?\d*$/,
                  message: "Please enter a valid number",
                },
              ]}
              validateTrigger={["onChange", "onBlur"]}
            >
              <Input
                addonAfter={<div>%</div>}
                className="custom-input"
                style={{ flex: 1, height: "44px" }}
                placeholder="0.00"
                type="number"
                step="0.01"
                onChange={(e) =>
                  handleInputChange("defaultRate", e.target.value)
                }
              />
            </Form.Item>
          </Col>

          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <Space>
                  Interest Calculation Basis
                  <Tooltip title="Interest calculation basis information">
                    <InfoCircleOutlined style={{ color: "#98A2B3" }} />
                  </Tooltip>
                </Space>
              }
              name="interestCalculationBasis"
              rules={[
                {
                  required: true,
                  message: "Please select interest calculation basis",
                },
              ]}
              validateTrigger={["onChange", "onBlur"]}
            >
              <Select
                placeholder="Actual/365 (daily accrual based on 365 days)"
                style={{ width: "100%", height: "44px" }}
                onChange={(value) =>
                  handleInputChange("interestCalculationBasis", value)
                }
                options={interbankTemplate?.interestCalculationBasisOptions?.map(
                  (basis) => ({
                    value: basis.value,
                    label: basis.text,
                  })
                )}
              />
            </Form.Item>
          </Col>

          <Col span={12} style={{ padding: "0px 8px" }}>
            <Form.Item
              label={
                <Space>
                  Interest Posting
                  <Tooltip title="Interest posting information">
                    <InfoCircleOutlined style={{ color: "#98A2B3" }} />
                  </Tooltip>
                </Space>
              }
              name="interestPostingType"
              rules={[
                { required: true, message: "Please select interest posting" },
              ]}
              validateTrigger={["onChange", "onBlur"]}
            >
              <Select
                placeholder="Select interest posting"
                style={{ width: "100%", height: "44px" }}
                onChange={(value) =>
                  handleInputChange("interestPostingType", value)
                }
                options={interbankTemplate?.interestPostingTypes?.map(
                  (posting) => ({
                    value: posting.value,
                    label: posting.text,
                  })
                )}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={6} style={{ padding: "0px 8px" }}>
            <Form.Item name={"allowTax"} style={{ marginBottom: 0 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <Switch
                  size="large"
                  checked={taxGroup}
                  onChange={handleTaxGroupChange}
                />
                <Space>Tax Group</Space>
              </div>
            </Form.Item>
          </Col>

          <Col span={6} style={{ padding: "0px 8px" }}>
            {taxGroup && (
              <Form.Item
                name="taxGroup"
                rules={[
                  {
                    required: taxGroup,
                    message: "Please select tax option",
                  },
                ]}
                validateTrigger={["onChange", "onBlur"]}
              >
                <Select
                  placeholder="Select option"
                  style={{ width: "100%", height: "44px" }}
                  onChange={handleTaxGroupSelectionChange}
                  virtual={false}
                  options={BOM.GetUnique(
                    interbankTemplate?.taxGroupOptions,
                    "id"
                  )?.map((option) => ({
                    value: option.id,
                    label: option.name,
                  }))}
                />
              </Form.Item>
            )}
          </Col>
        </Row>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "32px",
            gap: "12px",
          }}
        >
          <Button style={{ height: "44px" }} onClick={onPrevious}>
            Previous
          </Button>
          <Button
            type="primary"
            style={{ backgroundColor: "#9F1AB1", height: "44px" }}
            onClick={handleNext}
          >
            Next - Accounting
          </Button>
        </div>
      </Form>
    </Card>
  );
}
